import React, { useMemo, useRef, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import OfferModal from './OfferModal';
import { getApiUrl } from '../../../config/api';
import { getCachedJobs } from './interviewPrefetch';
import { toast } from 'sonner';
import { Heading, Text } from '@/Components/ui/typography';
import { Button } from '@/Components/ui/button';
import { Badge } from '@/Components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/Components/ui/card';
import { Skeleton } from '@/Components/ui/skeleton';
import { Inline } from '@/Components/ui/layout';
import { ScrollArea, ScrollBar } from '@/Components/ui/scroll-area';
import { creditsApi } from '../../api/creditsApi';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/Components/ui/select';
import { Input } from '@/Components/ui/input';
import { Label } from '@/Components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from '@/Components/ui/sheet';
import { Separator } from '@/Components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/Components/ui/dialog';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@/Components/ui/avatar';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/Components/ui/pagination';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@/Components/ui/tabs';
import { 
  Search, 
  Filter, 
  Calendar as CalendarIcon, 
  Users, 
  FileText,
  Loader2,
  CheckCircle2,
  X,
  MapPin,
  Clock,
  Award,
  UserCheck,
  List,
  LayoutGrid,
  BarChart3,
  Settings,
  FileCheck,
  Info,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  AlertCircle,
  Lock
} from 'lucide-react';
import { getColor } from '@/config/theme';
import { cn } from '@/lib/utils';
import { useCompanyAccess } from '@/HireWithUs/Context/CompanyAccessContext';

/** TEST TYPES (fixed order) */
const TESTS = ["screening", "personality", "behavioral", "coding", "aptitude"];

/** Plan entitlement + fixed-credit maps (mirror the backend cutover). */
const FEATURE_BY_TOKEN = {
  screening: "screening_test", personality: "screening_test",
  behavioral: "screening_test", coding: "coding_test", aptitude: "aptitude_test",
};
const ACTION_BY_TOKEN = {
  screening: "SCREENING_TEST", personality: "SCREENING_TEST",
  behavioral: "SCREENING_TEST", coding: "CODING_TEST", aptitude: "APTITUDE_TEST",
};
const CANON_TEST_COSTS = {
  SCREENING_TEST: 1, REALTIME_INTERVIEW: 4, CODING_TEST: 1,
  APTITUDE_TEST: 2, APTITUDE_TEMPLATE: 1, FLOW_TEST: 1,
};
// A test token is locked only when enforcement is ON and the plan excludes it.
function tokenLocked(ent, t) {
  if (!ent?.enforced) return false;
  const f = FEATURE_BY_TOKEN[t];
  return f ? ent.features?.[f] === false : false;
}
// Fixed credits to schedule one test of this token (from the plan, else default).
function tokenCost(ent, t) {
  const a = ACTION_BY_TOKEN[t];
  const costs = ent?.test_costs || CANON_TEST_COSTS;
  return a ? (costs[a] ?? CANON_TEST_COSTS[a] ?? 1) : 1;
}

/** Calendar view: per-test colours + labels (light & dark) */
const TEST_META = {
  screening: {
    label: "Screening", short: "Screening", dot: "bg-blue-500",
    pill: "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100 dark:bg-blue-950/50 dark:text-blue-200 dark:border-blue-900/70 dark:hover:bg-blue-900/50",
  },
  personality: {
    label: "Personality", short: "Personality", dot: "bg-violet-500",
    pill: "bg-violet-50 text-violet-700 border-violet-200 hover:bg-violet-100 dark:bg-violet-950/50 dark:text-violet-200 dark:border-violet-900/70 dark:hover:bg-violet-900/50",
  },
  behavioral: {
    label: "Behavioral", short: "Behavioral", dot: "bg-amber-500",
    pill: "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100 dark:bg-amber-950/50 dark:text-amber-200 dark:border-amber-900/70 dark:hover:bg-amber-900/50",
  },
  coding: {
    label: "Coding", short: "Coding", dot: "bg-emerald-500",
    pill: "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100 dark:bg-emerald-950/50 dark:text-emerald-200 dark:border-emerald-900/70 dark:hover:bg-emerald-900/50",
  },
  aptitude: {
    label: "Aptitude", short: "Aptitude", dot: "bg-teal-500",
    pill: "bg-teal-50 text-teal-700 border-teal-200 hover:bg-teal-100 dark:bg-teal-950/50 dark:text-teal-200 dark:border-teal-900/70 dark:hover:bg-teal-900/50",
  },
};

const CAL_WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const CAL_MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/** Local YYYY-MM-DD key (calendar buckets days in the viewer's timezone). */
function calDayKey(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** "9:30 AM" style time-only label. */
function formatTimeOnly(date) {
  if (!(date instanceof Date) || isNaN(date.getTime())) return "";
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? "PM" : "AM";
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${String(minutes).padStart(2, "0")} ${ampm}`;
}
// Keep the header, loading state, and rows on the same grid. On smaller
// screens the complete table scrolls instead of splitting tests or actions.
const CANDIDATE_TABLE_GRID = "grid min-w-[1100px] grid-cols-[minmax(280px,1.2fr)_minmax(440px,2fr)_90px_minmax(190px,auto)] gap-4";

// These are application lifecycle statuses, not test/interview statuses.  The
// candidate-list API already returns the application's current status, so the
// Scheduled Interviews screen can show the same source of truth as Hiring
// Pipelines without storing a second status in the browser.
const HIRING_PIPELINE_STATUS_META = {
  selected: {
    label: "Selected",
    className: "bg-green-600 hover:bg-green-600 text-white border-green-700",
  },
  "offer released": {
    label: "Offer Released",
    className: "bg-blue-600 hover:bg-blue-600 text-white border-blue-700",
  },
  "offer accepted": {
    label: "Offer Accepted",
    className: "bg-green-600 hover:bg-green-600 text-white border-green-700",
  },
  "background verification": {
    label: "Background Verification",
    className: "bg-violet-600 hover:bg-violet-600 text-white border-violet-700",
  },
  "background verification failed": {
    label: "Background Verification Failed",
    className: "bg-red-600 hover:bg-red-600 text-white border-red-700",
  },
  joined: {
    label: "Joined",
    className: "bg-teal-600 hover:bg-teal-600 text-white border-teal-700",
  },
  hired: {
    label: "Hired",
    className: "bg-green-600 hover:bg-green-600 text-white border-green-700",
  },
  "offer rejected": {
    label: "Offer Rejected",
    className: "bg-red-600 hover:bg-red-600 text-white border-red-700",
  },
  "offer expired": {
    label: "Offer Expired",
    className: "bg-slate-600 hover:bg-slate-600 text-white border-slate-700",
  },
};

function getHiringPipelineStatus(status, isHired = false) {
  if (isHired) return HIRING_PIPELINE_STATUS_META.hired;
  const normalizedStatus = String(status || "").trim().toLowerCase();
  return HIRING_PIPELINE_STATUS_META[normalizedStatus] || null;
}

function displayCandidateName(value) {
  return String(value || '').trim().toLowerCase().replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function HiringPipelineStatusBadge({ status, isHired, className }) {
  const statusMeta = getHiringPipelineStatus(status, isHired);
  if (!statusMeta) return null;

  const isCompletedPositiveStatus = statusMeta.label === "Hired" || statusMeta.label === "Selected";
  return (
    <Badge className={cn("text-xs font-semibold", statusMeta.className, className)}>
      {isCompletedPositiveStatus && <CheckCircle2 className="h-3 w-3 mr-1" />}
      {statusMeta.label}
    </Badge>
  );
}

/** helpers */
function weightedOverall(tests) {
  const done = TESTS.map((t) => tests[t]).filter((t) => t?.status === "completed");
  if (!done.length) return null;
  const avg = Math.round(done.reduce((a, b) => a + (Number(b.score) || 0), 0) / done.length);
  return avg;
}
function cx(...a) { return a.filter(Boolean).join(" "); }

function getCandidateTestId(candidate, testType) {
  const testIdKeyMap = {
    screening: 'screening_test_id',
    personality: 'personality_test_id',
    behavioral: 'behavioral_test_id',
    coding: 'coding_test_id',
    communication: 'communication_test_id',
    aptitude: 'aptitude_test_id',
  };

  const key = testIdKeyMap[testType];
  return key ? candidate?.[key] || null : null;
}

function formatShortDate(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  const day = date.getDate();
  const month = date.toLocaleDateString('en-US', { month: 'short' });
  
  // Add ordinal suffix (st, nd, rd, th)
  const getOrdinalSuffix = (n) => {
    if (n > 3 && n < 21) return 'th';
    switch (n % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };
  
  return `${day}${getOrdinalSuffix(day)} ${month}`;
}

function formatDateTime(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  
  // Check if date is valid
  if (isNaN(date.getTime())) return "";
  
  const day = date.getDate();
  const month = date.toLocaleDateString('en-US', { month: 'short' });
  
  // Format time in 12-hour format
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  const displayMinutes = minutes.toString().padStart(2, '0');
  
  return `${month} ${day}, ${displayHours}:${displayMinutes} ${ampm}`;
}

/** Skeleton Components */
function SkeletonBox({ width, height, className = "" }) {
  return (
    <Skeleton 
      className={cn(className)} 
      style={{ width, height }}
    />
  );
}

function SkeletonKPI() {
  return (
    <Card>
      <CardContent className="p-4">
        <Skeleton className="h-8 w-16 mb-2" />
        <Skeleton className="h-4 w-32" />
      </CardContent>
    </Card>
  );
}

function SkeletonJobButton() {
  return (
    <Skeleton className="h-auto py-2 px-4 rounded-md" style={{ width: '180px' }}>
      <div className="space-y-2">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-3 w-24" />
      </div>
    </Skeleton>
  );
}

function SkeletonJobCard() {
  return (
    <div className="skeleton-job">
      <SkeletonBox width="80%" height="20px" className="skeleton-job__title" />
      <SkeletonBox width="60%" height="14px" className="skeleton-job__meta" />
      <SkeletonBox width="40%" height="16px" className="skeleton-job__count" />
    </div>
  );
}


function SkeletonCandidateRow() {
  return (
    <div className={cn(CANDIDATE_TABLE_GRID, "items-start rounded-lg p-3")}>
      <div className="flex items-center gap-3">
        <Skeleton className="h-10 w-10 rounded-full" />
        <div className="space-y-2 flex-1">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-3 w-48" />
        </div>
      </div>
      <div className="flex items-center gap-2 flex-nowrap">
        {[1, 2, 3, 4].map(i => (
          <Skeleton key={i} className="h-6 w-20 rounded-full" />
        ))}
      </div>
      <div className="flex items-center justify-center">
        <Skeleton className="h-6 w-12" />
      </div>
      <div className="flex items-center justify-end gap-2">
        <Skeleton className="h-8 w-20 rounded-md" />
        <Skeleton className="h-8 w-20 rounded-md" />
      </div>
    </div>
  );
}

function SkeletonFilter() {
  return (
    <div className="skeleton-filter">
      <SkeletonBox width="16px" height="16px" className="skeleton-icon" />
      <SkeletonBox width="100%" height="20px" className="skeleton-select" />
    </div>
  );
}

function SkeletonTable() {
  return (
    <div className="skeleton-table">
      <div className="skeleton-table__head">
        <SkeletonBox width="80px" height="14px" />
        <SkeletonBox width="120px" height="14px" />
        <SkeletonBox width="60px" height="14px" />
      </div>
      {[1, 2, 3, 4, 5].map(i => (
        <SkeletonCandidateRow key={i} />
      ))}
    </div>
  );
}

function SkeletonInterviewPage() {
  return (
    <div className="flex flex-col w-full gap-6">
        {/* Header skeleton */}
        <div className="skeleton-header">
          <div>
            <SkeletonBox width="200px" height="28px" className="skeleton-title" />
            <SkeletonBox width="300px" height="16px" className="skeleton-subtitle" />
          </div>
          <div className="skeleton-view-toggle">
            {[1, 2, 3].map(i => (
              <SkeletonBox key={i} width="60px" height="32px" className="skeleton-toggle-btn" />
            ))}
          </div>
        </div>

        {/* Filters skeleton */}
        <div className="skeleton-filters">
          {[1, 2, 3, 4, 5].map(i => (
            <SkeletonFilter key={i} />
          ))}
        </div>

        {/* KPIs skeleton */}
        <div className="skeleton-kpis">
          {[1, 2, 3].map(i => (
            <SkeletonKPI key={i} />
          ))}
        </div>

        {/* Jobs skeleton */}
        <div className="skeleton-jobs">
          {[1, 2, 3, 4].map(i => (
            <SkeletonJobCard key={i} />
          ))}
        </div>

        {/* Panel skeleton */}
        <div className="skeleton-panel">
          <div className="skeleton-panel__head">
            <SkeletonBox width="250px" height="20px" />
          </div>
          <SkeletonTable />
        </div>
    </div>
  );
}

/** components */
function Chip({ label, data }) {
  const state = data?.status || "not_scheduled";

  let text = label;
  if (state === "completed" && typeof data?.score === "number") text += ` ${data.score}%`;
  if (state === "scheduled" && data?.scheduledAt) {
    text += ` • ${formatShortDate(data.scheduledAt)}`;
  }

  const getBadgeVariant = () => {
    switch (state) {
      case "completed": return "default";
      case "scheduled": return "secondary";
      case "in_progress": return "default";
      case "overdue": return "destructive";
      default: return "outline";
    }
  };

  return (
    <Badge 
      variant={getBadgeVariant()}
      className={cn(
        "capitalize text-xs",
        state === "completed" && "bg-green-100 text-green-800 border-green-200",
        state === "scheduled" && "bg-blue-100 text-blue-800 border-blue-200",
        state === "in_progress" && "bg-yellow-100 text-yellow-800 border-yellow-200",
        state === "overdue" && "bg-red-100 text-red-800 border-red-200"
      )}
    >
      {text}
    </Badge>
  );
}

/** A single scheduled session rendered as a clickable pill inside a day cell. */
function CalendarEventPill({ event, onOpen }) {
  const meta = TEST_META[event.test] || {};
  const isCompleted = event.status === "completed";
  const isOverdue = event.status === "overdue";
  const name = displayCandidateName(event.app?.candidate?.name) || "Candidate";
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onOpen(event); }}
      title={`${name} — ${meta.label || event.test} • ${formatTimeOnly(event.date)}${isCompleted ? " (completed)" : isOverdue ? " (overdue)" : ""}`}
      className={cn(
        "flex w-full items-center gap-1.5 rounded-md border px-1.5 py-1 text-left text-[11px] leading-tight transition-colors",
        meta.pill,
        isCompleted && "opacity-70"
      )}
    >
      <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", meta.dot)} />
      <span className="shrink-0 font-semibold tabular-nums">{formatTimeOnly(event.date)}</span>
      <span className="min-w-0 flex-1 truncate">{name}</span>
      {isCompleted && <CheckCircle className="h-3 w-3 shrink-0" />}
      {isOverdue && <AlertCircle className="h-3 w-3 shrink-0 text-red-600 dark:text-red-400" />}
    </button>
  );
}

function KPI({ label, value, type }) {
  const getCardStyle = () => {
    switch (type) {
      case 'scheduled':
        return 'border-yellow-200 bg-yellow-50';
      case 'completed':
        return 'border-green-200 bg-green-50';
      case 'overdue':
        return 'border-red-200 bg-red-50';
      case 'hired':
        return 'border-blue-200 bg-blue-50';
      default:
        return 'border-border bg-muted';
    }
  };

  return (
    <Card className={cn("border", getCardStyle())}>
      <CardContent className="p-4">
        <div className="text-2xl font-bold text-foreground mb-1">{value}</div>
        <div className="text-sm text-muted-foreground">{label}</div>
      </CardContent>
    </Card>
  );
}

function Pager({ total, page, pageSize, onPage }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const go = (p) => onPage(Math.min(Math.max(1, p), pages));
  const windowPages = Array.from({ length: pages }, (_, i) => i + 1)
    .filter(p => p === 1 || p === pages || (p >= page - 2 && p <= page + 2));
  
  if (pages <= 1) return null;
  
  return (
    <Pagination>
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious 
            type="button"
            onClick={() => page > 1 && go(page - 1)}
            disabled={page === 1}
          />
        </PaginationItem>
        
      {windowPages.map((p, i) => {
        const prev = windowPages[i - 1];
        const gap = prev && p - prev > 1;
        return (
          <React.Fragment key={p}>
              {gap && (
                <PaginationItem>
                  <PaginationEllipsis />
                </PaginationItem>
              )}
              <PaginationItem>
                <PaginationLink
                  type="button"
                  onClick={() => go(p)}
                  isActive={p === page}
                >
                  {p}
                </PaginationLink>
              </PaginationItem>
          </React.Fragment>
        );
      })}
        
        <PaginationItem>
          <PaginationNext 
            type="button"
            onClick={() => page < pages && go(page + 1)}
            disabled={page === pages}
          />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
}

/** main */
export default function InterviewSchedule() {
  const navigate = useNavigate();
  const { can } = useCompanyAccess();
  
  // job-first
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [jobFilter, setJobFilter] = useState("all");
  const [testFilter, setTestFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [hiredFilter, setHiredFilter] = useState("all"); // all | hired | not_hired
  const [searchQuery, setSearchQuery] = useState(""); // Search by candidate name
  const [view, setView] = useState("list"); // list | kanban | calendar
  const [calMode, setCalMode] = useState("month"); // month | week
  const [calCursor, setCalCursor] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [calDay, setCalDay] = useState(null); // { date, events } for the "day detail" dialog
  const [drawer, setDrawer] = useState(null); // selected application for drawer
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [jobsError, setJobsError] = useState("");
  const [pageSize, setPageSize] = useState(100); // Default to 100 to show more candidates
  const [offerModalOpen, setOfferModalOpen] = useState(false);
  const [offerCandidate, setOfferCandidate] = useState(null);

  // State for API data
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [totalCandidates, setTotalCandidates] = useState(0);
  
  // Cache for full candidate lists per job (for client-side pagination)
  const candidateCache = React.useRef(new Map());

  // Cache hired-candidates API results briefly to avoid refetch on rapid job switching
  const hiredCandidatesCacheRef = useRef(new Map());

  // Prevent overlapping fetches when switching jobs quickly
  const candidatesAbortRef = useRef(null);
  const latestCandidatesFetchRef = useRef(0);
  const candidatesInFlightRef = useRef({ key: null });

  // State for reschedule modal
  const [rescheduleModal, setRescheduleModal] = useState({
    isOpen: false,
    testType: null,
    candidate: null,
    currentTime: null,
    newTime: null
  });

  // Plan entitlements (enforced flag + feature map + fixed test costs).
  // Declared BEFORE the reschedule cost memo below, which reads `ent`.
  const [ent, setEnt] = useState(null);

  // Real reschedule cost + balance (fixed-per-action model, from /api/entitlements/me).
  // The server also enforces + 402s; this drives the widget + the disabled state.
  const rescheduleCost = useMemo(
    () => (rescheduleModal?.isOpen && rescheduleModal?.testType ? tokenCost(ent, rescheduleModal.testType) : 0),
    [ent, rescheduleModal?.isOpen, rescheduleModal?.testType],
  );
  const rescheduleAvailable = Number(ent?.balance ?? 0);
  const rescheduleCanAfford = !ent?.enforced || rescheduleAvailable >= rescheduleCost;

  // Loading states for actions
  const [actionLoading, setActionLoading] = useState({
    reschedule: false,
    cancel: false,
    hire: false
  });

  // State for hire confirmation modal
  const [hireModal, setHireModal] = useState({
    isOpen: false,
    candidate: null,
    jobId: null
  });

  // Fetch entitlements (state declared above, before the reschedule cost memo).
  useEffect(() => {
    const cid = localStorage.getItem('companyAccountId');
    if (!cid) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(getApiUrl(`/api/entitlements/me?company_id=${encodeURIComponent(cid)}`), { credentials: 'include' });
        const data = await res.json();
        if (!cancelled) setEnt(data || null);
      } catch (e) {
        console.error('[entitlements]', e);
        if (!cancelled) setEnt(null);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Get company credentials
  const getCompanyEmail = () => {
    const employerEmail = localStorage.getItem('employer_email');
    if (employerEmail) {
      return employerEmail;
    }
    console.error('No employer email found in localStorage');
    return null;
  };

  const getCompanyId = () => {
    const companyId = localStorage.getItem('companyAccountId');
    if (companyId) {
      return companyId;
    }
    console.error('No company ID found in localStorage');
    return null;
  };

  // Fetch job titles from API (uses prefetch cache when available for instant load)
  const fetchJobTitles = useCallback(async () => {
    const cached = getCachedJobs();
    if (cached?.length) {
      setJobsError("");
      setJobs(cached);
      if (!selectedJobId) {
        setSelectedJobId(cached[0].id);
        setJobFilter(cached[0].id);
      }
      setJobsLoading(false);
      // Cache is considered fresh (see interviewPrefetch.js TTL),
      // so skip the network request to avoid an extra ~2s API call.
      return;
    }

    setJobsLoading(true);
    setJobsError("");

    try {
      const companyEmail = getCompanyEmail();
      const companyId = getCompanyId();
      
      if (!companyEmail || !companyId) {
        console.error('Missing company credentials');
        setJobsError('Your employer session is missing. Please sign in again.');
        return;
      }

      const response = await fetch(getApiUrl(`/api/get_jd?company_email=${encodeURIComponent(companyEmail)}&companyId=${companyId}`), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Failed to load job postings (HTTP ${response.status})`);
      }

      const data = await response.json();
      if (!Array.isArray(data)) {
        throw new Error('The job postings response was invalid');
      }

      const transformedJobs = data.map(job => ({
        id: job.id || job.job_id,
        title: job.job_title,
        location: job.location || "Remote-first",
        level: job.job_level || "Junior, Mid-Level, Senior"
      }));

      setJobs(transformedJobs);
      if (transformedJobs.length > 0 && !selectedJobId) {
        setSelectedJobId(transformedJobs[0].id);
        setJobFilter(transformedJobs[0].id);
      }
    } catch (error) {
      console.error('Error fetching job titles:', error);
      setJobsError('Unable to load job postings. Please refresh the page or sign in again.');
    } finally {
      setJobsLoading(false);
    }
  }, [selectedJobId]);

  // Function to verify and update hired status for candidates using a single batched API call
  const verifyHiredStatus = useCallback(async (candidates, jobId) => {
    if (!candidates || candidates.length === 0) return candidates;

    const companyEmail = getCompanyEmail();
    const companyId = getCompanyId();

    if (!companyEmail) {
      console.error('Missing company email while verifying hired status');
      return candidates;
    }

    try {
      const cacheTtlMs = 30_000;
      const cacheKey = `${companyId || ''}:${companyEmail}:${jobId && jobId !== 'all' ? jobId : 'all'}`;
      const cached = hiredCandidatesCacheRef.current.get(cacheKey);
      const now = Date.now();
      if (cached && now - cached.ts < cacheTtlMs && Array.isArray(cached.hired_candidates)) {
        const hiredList = cached.hired_candidates;

        const byJobAndEmail = new Map();
        const byEmail = new Map();

        hiredList.forEach((hired) => {
          const email = (hired.candidate_email || '').toLowerCase();
          const hJobId = String(hired.job_id ?? '').trim();
          const hiredDate = hired.hired_date || null;

          if (email) {
            if (hJobId) {
              byJobAndEmail.set(`${hJobId}:${email}`, hiredDate);
            }
            if (!byEmail.has(email)) {
              byEmail.set(email, hiredDate);
            }
          }
        });

        return candidates.map((app) => {
          const email = (app?.candidate?.id || '').toLowerCase();
          const appJobId = app?.jobId != null ? String(app.jobId).trim() : '';

          if (!email) return app;

          let hiredAt = null;
          if (appJobId) {
            hiredAt = byJobAndEmail.get(`${appJobId}:${email}`) || null;
          }
          if (!hiredAt) {
            hiredAt = byEmail.get(email) || null;
          }
          if (!hiredAt) return app;

          return { ...app, isHired: true, hiredAt: hiredAt || app.hiredAt };
        });
      }

      // Build URL for batched hired-candidates fetch
      let url = getApiUrl(
        `/api/get_hired_candidates?company_email=${encodeURIComponent(companyEmail)}`
      );

      // When viewing a specific job, scope to that job for faster queries
      if (jobId && jobId !== 'all') {
        url += `&job_id=${jobId}`;
      }

      if (companyId) {
        url += `&company_id=${companyId}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        console.error('Failed to fetch hired candidates for status verification');
        return candidates;
      }

      const data = await response.json();
      if (!data?.success || !Array.isArray(data.hired_candidates)) {
        return candidates;
      }

      const hiredList = data.hired_candidates;
      hiredCandidatesCacheRef.current.set(cacheKey, { ts: now, hired_candidates: hiredList });

      // Build fast lookup maps for hired candidates
      const byJobAndEmail = new Map();
      const byEmail = new Map();

      hiredList.forEach((hired) => {
        const email = (hired.candidate_email || '').toLowerCase();
        const hJobId = String(hired.job_id ?? '').trim();
        const hiredDate = hired.hired_date || null;

        if (email) {
          if (hJobId) {
            byJobAndEmail.set(`${hJobId}:${email}`, hiredDate);
          }
          // Also keep a global email map so "All Jobs" view can still mark hires
          if (!byEmail.has(email)) {
            byEmail.set(email, hiredDate);
          }
        }
      });

      // Merge hired info into the candidate applications
      const updatedCandidates = candidates.map((app) => {
        const email = (app?.candidate?.id || '').toLowerCase();
        const appJobId = app?.jobId != null ? String(app.jobId).trim() : '';

        if (!email) {
          return app;
        }

        let hiredAt = null;

        if (appJobId) {
          hiredAt = byJobAndEmail.get(`${appJobId}:${email}`) || null;
        }

        // In "All Jobs" view or if jobId mapping fails, fall back to email-only lookup
        if (!hiredAt) {
          hiredAt = byEmail.get(email) || null;
        }

        if (!hiredAt) {
          return app;
        }

        return {
          ...app,
          isHired: true,
          hiredAt: hiredAt || app.hiredAt,
        };
      });

      return updatedCandidates;
    } catch (error) {
      console.error('Error verifying hired status:', error);
      return candidates;
    }
  }, []);

  // Fetch candidates for a specific job from API
  // IMPORTANT: Uses different endpoints based on selection:
  // - For specific job: /api/candidatelist?jobId={jobId} - properly scopes test data to that job
  // - For "All Jobs": /api/candidatelist_without_jobID - aggregates across all jobs
  // This ensures candidates who applied to multiple jobs show correct test data per job
  const fetchCandidates = useCallback(async (jobId, pageNum = 1, forceRefresh = false) => {
    if (!jobId) return;

    // De-dupe identical requests (React StrictMode / rapid re-renders can call effects twice)
    const requestKey = `${jobId}:${pageNum}:${pageSize}`;
    if (!forceRefresh && candidatesInFlightRef.current.key === requestKey) {
      return;
    }
    
    // For specific jobs, check cache first (since API returns all candidates)
    if (jobId !== "all" && !forceRefresh && candidateCache.current.has(jobId)) {
      const cachedData = candidateCache.current.get(jobId);
      
      // Show all cached candidates (no pagination)
      setApplications(cachedData);
      setTotalCandidates(cachedData.length);
      setLoading(false);
      return;
    }

    // Session cache (survives reload): stale-while-revalidate for snappy first paint
    if (jobId !== "all" && !forceRefresh) {
      try {
        const key = `interviewSchedule:candidates:${jobId}`;
        const raw = sessionStorage.getItem(key);
        if (raw) {
          const parsed = JSON.parse(raw);
          const ts = parsed?.ts;
          const data = parsed?.data;
          if (Array.isArray(data) && data.length) {
            candidateCache.current.set(jobId, data);
            setApplications(data);
            setTotalCandidates(data.length);
            setLoading(false);

            // If it's fresh enough, skip network entirely
            const ttlMs = 30_000;
            if (typeof ts === 'number' && Date.now() - ts < ttlMs) {
              return;
            }
          }
        }
      } catch {
        // ignore sessionStorage issues
      }
    }
    
    candidatesInFlightRef.current.key = requestKey;
    setLoading(true);
    const fetchId = Date.now();
    try {
      // Abort any previous in-flight candidate request
      if (candidatesAbortRef.current) {
        try { candidatesAbortRef.current.abort(); } catch {}
      }
      const abortController = new AbortController();
      candidatesAbortRef.current = abortController;
      latestCandidatesFetchRef.current = fetchId;

      const companyEmail = getCompanyEmail();
      const companyId = getCompanyId();
      
      if (!companyEmail || !companyId) {
        console.error('Missing company credentials');
        return;
      }

      let url;
      let response;
      
      // Use different API endpoints based on whether "all" or specific job is selected
      if (jobId === "all") {
        // For "All Jobs", use the without_jobID endpoint
        // Use pageSize to fetch large batches (no pagination UI, just load all)
        url = getApiUrl(`/api/candidatelist_without_jobID?company_email=${encodeURIComponent(companyEmail)}&companyId=${companyId}&page=1&per_page=${pageSize}`);
        
        response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          signal: abortController.signal,
        });
      } else {
        // For specific job, use the candidatelist endpoint with jobId
        // This properly scopes test data to the specific job (no pagination params needed)
        url = getApiUrl(`/api/candidatelist?jobId=${jobId}`);
        
        response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          signal: abortController.signal,
        });
      }

      if (response.ok) {
        const data = await response.json();
        
        // Handle different response formats
        const candidatesArray = Array.isArray(data) ? data : (data.candidates || []);
        
        if (candidatesArray && Array.isArray(candidatesArray)) {
          const hasText = (v) => typeof v === 'string' && v.trim() !== '';

          // Transform candidate data to match our component structure
          const transformedApplications = candidatesArray.map(candidate => {
            // Check if any test is scheduled or completed (completed tests mean they were scheduled at some point)
            // More robust checking for scheduled times and scores
            const hasScheduledTests =
              hasText(candidate.screening_scheduled_time) ||
              hasText(candidate.personality_scheduled_time) ||
              hasText(candidate.behavioral_scheduled_time) ||
              hasText(candidate.coding_scheduled_time) ||
              hasText(candidate.aptitude_scheduled_time) ||
              (candidate.screening_overall_score !== null && candidate.screening_overall_score !== undefined) ||
              (candidate.personality_overall_score !== null && candidate.personality_overall_score !== undefined) ||
              (candidate.behavioral_overall_score !== null && candidate.behavioral_overall_score !== undefined) ||
              (candidate.coding_overall_score !== null && candidate.coding_overall_score !== undefined) ||
              (candidate.aptitude_overall_score !== null && candidate.aptitude_overall_score !== undefined);

            // (Removed noisy per-candidate debug logging; it slows down large lists significantly)

            const tests = {
              screening: {
                status: hasText(candidate.screening_scheduled_time) ? 
                  ((candidate.screening_overall_score !== null && candidate.screening_overall_score !== undefined) ? "completed" : "scheduled") : "not_scheduled",
                score: candidate.screening_overall_score,
                scheduledAt: candidate.screening_scheduled_time,
                completedAt: (candidate.screening_overall_score !== null && candidate.screening_overall_score !== undefined) ? candidate.updated_at : null
              },
              personality: {
                status: hasText(candidate.personality_scheduled_time) ? 
                  ((candidate.personality_overall_score !== null && candidate.personality_overall_score !== undefined) ? "completed" : "scheduled") : "not_scheduled",
                score: candidate.personality_overall_score,
                scheduledAt: candidate.personality_scheduled_time,
                completedAt: (candidate.personality_overall_score !== null && candidate.personality_overall_score !== undefined) ? candidate.updated_at : null
              },
              behavioral: {
                status: hasText(candidate.behavioral_scheduled_time) ? 
                  ((candidate.behavioral_overall_score !== null && candidate.behavioral_overall_score !== undefined) ? "completed" : "scheduled") : "not_scheduled",
                score: candidate.behavioral_overall_score,
                scheduledAt: candidate.behavioral_scheduled_time,
                completedAt: (candidate.behavioral_overall_score !== null && candidate.behavioral_overall_score !== undefined) ? candidate.updated_at : null
              },
              coding: {
                status: hasText(candidate.coding_scheduled_time) ?
                  ((candidate.coding_overall_score !== null && candidate.coding_overall_score !== undefined) ? "completed" : "scheduled") : "not_scheduled",
                score: candidate.coding_overall_score,
                scheduledAt: candidate.coding_scheduled_time,
                completedAt: (candidate.coding_overall_score !== null && candidate.coding_overall_score !== undefined) ? candidate.updated_at : null
              },
              aptitude: {
                status: hasText(candidate.aptitude_scheduled_time) ?
                  ((candidate.aptitude_overall_score !== null && candidate.aptitude_overall_score !== undefined) ? "completed" : "scheduled") : "not_scheduled",
                score: candidate.aptitude_overall_score,
                scheduledAt: candidate.aptitude_scheduled_time,
                completedAt: (candidate.aptitude_overall_score !== null && candidate.aptitude_overall_score !== undefined) ? candidate.updated_at : null
              }
            };

            // Check hired status - use API value or default to false
            // Handle various formats: boolean true, integer 1, string '1', string 'true'
            const isHiredRaw = candidate.is_hired;
            const isHired = isHiredRaw === true || 
                           isHiredRaw === 1 || 
                           isHiredRaw === '1' || 
                           isHiredRaw === 'true' ||
                           (typeof isHiredRaw === 'string' && isHiredRaw.toLowerCase() === 'true');
            const hiredAt = candidate.hired_at || null;
            const lifecycleStatus = String(candidate.status || '').trim();
            const isSelected = lifecycleStatus.toLowerCase() === 'selected';
            
            // (Removed noisy per-candidate debug logging; it slows down large lists significantly)

            return {
              id: candidate.email || candidate.candidate_email || candidate.id,
              // Use the jobId we're fetching for (specific job) or the candidate's job_id (for "all")
              jobId: jobId !== "all" ? jobId : (candidate.job_id || "unknown"),
              candidate: {
                id: candidate.email || candidate.candidate_email || candidate.id,
                name: candidate.full_name || candidate.name || 'Candidate Name',
                location: candidate.currentLocation || candidate.current_location || candidate.location || 'Remote',
                // Profile/AI summary fields
                overall_summary: candidate.overall_summary || null,
                strengths:       candidate.strengths       || null,
                // Store test IDs for results lookup
                screening_test_id:     candidate.screening_test_id     || null,
                personality_test_id:   candidate.personality_test_id   || null,
                behavioral_test_id:    candidate.behavioral_test_id    || null,
                coding_test_id:        candidate.coding_test_id        || null,
                communication_test_id: candidate.communication_test_id || null,
                aptitude_test_id:      candidate.aptitude_test_id      || null,
                // Store scheduled times for fallback display
                screening_scheduled_time:     candidate.screening_scheduled_time     || null,
                personality_scheduled_time:   candidate.personality_scheduled_time   || null,
                behavioral_scheduled_time:    candidate.behavioral_scheduled_time    || null,
                coding_scheduled_time:        candidate.coding_scheduled_time        || null,
                communication_scheduled_time: candidate.communication_scheduled_time || null,
                aptitude_scheduled_time:      candidate.aptitude_scheduled_time      || null,
              },
              appliedAt: candidate.created_at
                ? (typeof candidate.created_at === 'string' && candidate.created_at.length >= 10
                    ? candidate.created_at.slice(0, 10)
                    : new Date(candidate.created_at).toISOString().split('T')[0])
                : 'Unknown',
              tests: tests,
              hasScheduledTests: hasScheduledTests,
              isHired: isHired,
              hiredAt: hiredAt,
              isSelected: isSelected,
              lifecycleStatus: lifecycleStatus
            };
          });
          
          // Render ASAP: do not block on hired-status verification (2nd API call)
          if (latestCandidatesFetchRef.current !== fetchId) return;

          if (jobId !== "all") {
            // Cache the full dataset for this specific job immediately
            candidateCache.current.set(jobId, transformedApplications);
            try {
              sessionStorage.setItem(
                `interviewSchedule:candidates:${jobId}`,
                JSON.stringify({ ts: Date.now(), data: transformedApplications })
              );
            } catch {
              // ignore storage quota/errors
            }
          }

          setApplications(transformedApplications);
          setTotalCandidates(transformedApplications.length);
          setLoading(false);

          // Background: verify hired status and reconcile credits without delaying first paint
          Promise.resolve()
            .then(async () => {
              const verifiedApplications = await verifyHiredStatus(transformedApplications, jobId);
              if (latestCandidatesFetchRef.current !== fetchId) return null;
              return verifiedApplications;
            })
            .then((verifiedApplications) => {
              if (!verifiedApplications) return;

              // Update cache + UI with hired flags once available
              if (jobId !== "all") {
                candidateCache.current.set(jobId, verifiedApplications);
              }
              setApplications(verifiedApplications);
              setTotalCandidates(verifiedApplications.length);

              // Credits reconciliation (demo) – defer to keep UI snappy
              setTimeout(() => {
                try {
                  const companyId = getCompanyId();
                  if (!companyId) return;

                  verifiedApplications.forEach((app) => {
                    const jobIdForApp = app.jobId;
                    const candidateIdOrEmail = app?.candidate?.id;
                    const tests = app?.tests || {};

                    if (!candidateIdOrEmail || !jobIdForApp) return;

                    Object.entries(tests).forEach(([testType, test]) => {
                      if (test?.status !== 'completed') return;

                      const refKey = creditsApi.makeRefKey({
                        companyId,
                        jobId: jobIdForApp,
                        candidateIdOrEmail,
                        testType,
                      });

                      creditsApi.consumeBestEffort({
                        companyId,
                        refKey,
                        match: { jobId: jobIdForApp, candidateIdOrEmail, testType },
                      });

                      if (testType === 'personality' || testType === 'behavioral') {
                        creditsApi.consumeBestEffort({
                          companyId,
                          match: { jobId: jobIdForApp, candidateIdOrEmail, testType: 'all-test' },
                        });
                      }
                    });
                  });
                } catch (e) {
                  console.warn('Credits reconciliation failed', e);
                }
              }, 0);
            })
            .catch((e) => {
              // Never block UI on background tasks
              console.warn('Post-fetch background work failed', e);
            });
          
          // Show all candidates without pagination (for ranking purposes)
          if (jobId === "all") {
            // already set above (fast path)
          } else {
            // already set above (fast path)
          }
        } else {
          setApplications([]);
          setTotalCandidates(0);
        }
      } else {
        setApplications([]);
        setTotalCandidates(0);
      }
    } catch (error) {
      if (error?.name === 'AbortError') {
        return;
      }
      console.error('Error fetching candidates:', error);
      setApplications([]);
      setTotalCandidates(0);
    } finally {
      if (candidatesInFlightRef.current.key === requestKey) {
        candidatesInFlightRef.current.key = null;
      }
      // Only clear loading for the latest request to avoid flicker on rapid switching
      if (latestCandidatesFetchRef.current === fetchId) {
        setLoading(false);
      }
    }
  }, [pageSize, verifyHiredStatus]);

  // Function to schedule a new test
  const handleScheduleTest = async (testType, candidate) => {
    if (!testType || !candidate) {
      toast.error('Invalid test or candidate data');
      return;
    }
    
    if (!selectedJobId) {
      toast.error('No job selected. Please select a job first.');
      return;
    }

    // Open a modal to select schedule time
    setRescheduleModal({
      isOpen: true,
      testType,
      candidate,
      currentTime: null, // No current time for new scheduling
      newTime: null
    });
  };

  // Function to reschedule a test
  const handleRescheduleTest = async (testType, candidate, currentTime) => {
    if (!testType || !candidate) {
      toast.error('Invalid test or candidate data');
      return;
    }
    
    // Check if the test is actually scheduled
    if (!currentTime) {
      toast.error('This test is not scheduled yet. Please schedule it first.');
      return;
    }
    
    setRescheduleModal({
      isOpen: true,
      testType,
      candidate,
      currentTime,
      newTime: currentTime
    });
  };

  // Function to submit reschedule
  const submitReschedule = async () => {
          if (!rescheduleModal.newTime) {
        toast.error('Please select a new time');
        return;
      }

      // Check if the new time is in the future
      const newTimeDate = new Date(rescheduleModal.newTime);
      const now = new Date();
      if (newTimeDate <= now) {
        toast.error('Please select a future time for rescheduling');
        return;
      }

      if (!selectedJobId) {
        toast.error('No job selected. Please select a job first.');
        return;
      }

    setActionLoading(prev => ({ ...prev, reschedule: true }));

    try {
      const companyEmail = getCompanyEmail();
      const companyId = getCompanyId();
      
      if (!companyEmail || !companyId) {
        toast.error('Missing company credentials. Please log in again.');
        return;
      }

      // If a previous block exists for this test/candidate/job, release it so rescheduling doesn't double-block.
      creditsApi.releaseBlockedMatching({
        companyId,
        match: {
          jobId: selectedJobId,
          candidateIdOrEmail: rescheduleModal.candidate.id,
          testType: rescheduleModal.testType,
        },
      });

      // Map test types to the format expected by the backend
      const testTypeMap = {
        'screening': '/screening',
        'personality': '/personality-test',
        'behavioral': '/behavioral-test',
        'coding': '/icoding',
        'aptitude': '/aptitude'
      };

      const backendTestType = testTypeMap[rescheduleModal.testType];
      if (!backendTestType) {
        toast.error('Invalid test type for rescheduling');
        return;
      }

      // Plan gate: block a test the company's plan doesn't include (enforcement on).
      if (tokenLocked(ent, rescheduleModal.testType)) {
        toast.error(`${rescheduleModal.testType} isn't included in your ${ent?.plan_name || 'current'} plan. Upgrade to schedule it.`);
        return;
      }

      // Use the schedule endpoint to reschedule (this will create a new test with new time)
      const formData = new FormData();
      formData.append('selected_users', rescheduleModal.candidate.id);
      formData.append('jobId', selectedJobId);
      formData.append('difficulty', 'medium');
      formData.append('scheduledTime', rescheduleModal.newTime);
      formData.append('test_type', backendTestType);
      formData.append('topic', 'General');
      formData.append('companyId', companyId);
      
      // Add language parameter for coding tests
      if (rescheduleModal.testType === 'coding') {
        formData.append('language', 'python'); // Default language
      }

      // Aptitude reschedule regenerates the test; default to AI-generated
      // (source_mode 'ai' => APTITUDE_TEST = 2 cr), matching the displayed cost.
      if (rescheduleModal.testType === 'aptitude') {
        formData.append('aptitude_config', JSON.stringify({ source_mode: 'ai' }));
      }

      // Real affordability pre-check (fixed-per-action cost). The server also
      // enforces and returns 402/403, handled below.
      const needCredits = tokenCost(ent, rescheduleModal.testType);
      if (ent?.enforced && Number(ent?.balance ?? 0) < needCredits) {
        toast.error(`Not enough credits. Need ${needCredits}, available ${Number(ent?.balance ?? 0)}.`);
        return;
      }

      const response = await fetch(getApiUrl('/api/schedule_test_jobId'), {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        toast.success('Test rescheduled successfully!');

        // Clear cache for this job to force refresh
        candidateCache.current.delete(selectedJobId);
        // Refresh the candidates list
        fetchCandidates(selectedJobId, page, true);
        setRescheduleModal({ isOpen: false, testType: null, candidate: null, currentTime: null, newTime: null });
      } else {
        let errorData = null;
        try { errorData = await response.clone().json(); } catch (e) { /* not JSON */ }
        if (response.status === 403 && errorData?.upgrade_required) {
          toast.error(`${rescheduleModal.testType} isn't in your ${ent?.plan_name || 'current'} plan. Upgrade to schedule it.`);
        } else if (response.status === 402) {
          toast.error(`Not enough credits${errorData?.required ? ` (needs ${errorData.required} cr)` : ''}. Please top up.`);
        } else if (errorData?.reason === 'monthly_limit_reached') {
          toast.error(errorData.error, { duration: 12000 });
        } else {
          const errorMessage = errorData?.error || errorData?.message || `HTTP ${response.status}: ${response.statusText}`;
          toast.error(`Failed to reschedule test: ${errorMessage}`);
        }
        console.error('Reschedule error response:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error rescheduling test:', error);
      toast.error('Error rescheduling test. Please try again.');
    } finally {
      setActionLoading(prev => ({ ...prev, reschedule: false }));
    }
  };

  // Function to cancel a test
  const handleCancelTest = async (testType, candidate) => {
    if (!testType || !candidate) {
      toast.error('Invalid test or candidate data');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to cancel the ${testType} test for ${candidate.name}?`)) {
      return;
    }

    setActionLoading(prev => ({ ...prev, cancel: true }));

    try {
      const companyEmail = getCompanyEmail();
      const companyId = getCompanyId();
      const testId = getCandidateTestId(candidate, testType);
      const candidateEmail = candidate.id;
      const targetJobId = drawer?.candidate?.id === candidate.id ? drawer?.jobId : selectedJobId;
      const testLabel = testType.charAt(0).toUpperCase() + testType.slice(1);
      
      if (!companyEmail || !companyId) {
        toast.error('Missing company credentials. Please log in again.');
        return;
      }

      if (!testId || !candidateEmail) {
        toast.error(`Missing ${testType} test details. Please refresh and try again.`);
        return;
      }

      const response = await fetch(getApiUrl('/api/delete_test'), {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          test_id: testId,
          candidate_email: candidateEmail,
        })
      });

      if (response.ok) {
        creditsApi.releaseBlockedMatching({
          companyId,
          match: {
            jobId: targetJobId,
            candidateIdOrEmail: candidateEmail,
            testType,
          },
        });

        const testIdKey = `${testType}_test_id`;
        const scheduledTimeKey = `${testType}_scheduled_time`;
        const clearTestState = (app) => {
          const nextTests = {
            ...app.tests,
            [testType]: {
              ...app.tests?.[testType],
              status: 'not_scheduled',
              score: null,
              scheduledAt: null,
              completedAt: null,
            },
          };

          const hasScheduledTests = Object.values(nextTests).some(
            (test) => test?.status && test.status !== 'not_scheduled'
          );

          return {
            ...app,
            candidate: {
              ...app.candidate,
              [testIdKey]: null,
              [scheduledTimeKey]: null,
            },
            tests: nextTests,
            hasScheduledTests,
          };
        };

        setApplications(prevApps =>
          prevApps.map(app => {
            const sameCandidate = app.candidate.id === candidateEmail;
            const sameJob = targetJobId == null || app.jobId?.toString() === targetJobId?.toString();
            return sameCandidate && sameJob ? clearTestState(app) : app;
          })
        );

        setDrawer(prevDrawer => {
          if (!prevDrawer) return prevDrawer;
          const sameCandidate = prevDrawer.candidate.id === candidateEmail;
          const sameJob = targetJobId == null || prevDrawer.jobId?.toString() === targetJobId?.toString();
          return sameCandidate && sameJob ? clearTestState(prevDrawer) : prevDrawer;
        });

        candidateCache.current.clear();
        fetchCandidates(selectedJobId, page, true);
        toast.success(`${testLabel} test cancelled successfully!`);
      } else {
        let errorMessage = 'Unknown error';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch (e) {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        toast.error(`Failed to cancel test: ${errorMessage}`);
      }
    } catch (error) {
      console.error('Error cancelling test:', error);
      toast.error('Error cancelling test. Please try again.');
    } finally {
      setActionLoading(prev => ({ ...prev, cancel: false }));
    }
  };

  // Function to mark candidate as hired
  const handleMarkAsHired = async (candidate, jobId) => {
    if (!candidate || !jobId) {
      toast.error('Invalid candidate or job data');
      return;
    }

    // Open confirmation modal
    setHireModal({
      isOpen: true,
      candidate,
      jobId
    });
  };

  // Function to confirm and submit hire status
  const confirmHireCandidate = async () => {
    if (!hireModal.candidate || !hireModal.jobId) {
      toast.error('Invalid candidate or job data');
      return;
    }

    setActionLoading(prev => ({ ...prev, hire: true }));

    try {
      // The backend reads company ownership from the authenticated session.
      // Do not trust client-provided company identifiers for this transition.
      const selectionData = {
        candidate_email: hireModal.candidate.id,
        job_id: hireModal.jobId,
      };

      const response = await fetch(getApiUrl('/api/mark_candidate_hired'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(selectionData)
      });

      if (response.ok) {
        toast.success(`${hireModal.candidate.name} has been marked as Selected.`);

        // Clear cache for this job to force refresh
        candidateCache.current.delete(hireModal.jobId);
        // Refresh the candidates list to get the server-authoritative status.
        fetchCandidates(selectedJobId, page, true);
        
        // Update local state so this candidate cannot be selected twice.
        setApplications(prevApps => 
          prevApps.map(app => {
            if (app.candidate.id === hireModal.candidate.id && 
                app.jobId.toString() === hireModal.jobId.toString()) {
              return {
                ...app,
                isSelected: true,
                lifecycleStatus: 'Selected'
              };
            }
            return app;
          })
        );
        
        // Update drawer if it's the same candidate
        if (drawer && drawer.candidate.id === hireModal.candidate.id && 
            drawer.jobId.toString() === hireModal.jobId.toString()) {
          setDrawer(prev => prev ? {
            ...prev,
            isSelected: true,
            lifecycleStatus: 'Selected'
          } : null);
        }
        
        // Close modal and drawer
        setHireModal({ isOpen: false, candidate: null, jobId: null });
        // Keep drawer open briefly to show the badge
        setTimeout(() => setDrawer(null), 2000);
      } else {
        let errorMessage = 'Unknown error';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || 'Unknown error';
        } catch (e) {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        toast.error(`Failed to mark candidate as Selected: ${errorMessage}`);
        console.error('Selection error response:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error marking candidate as Selected:', error);
      toast.error('Error marking candidate as Selected. Please try again.');
    } finally {
      setActionLoading(prev => ({ ...prev, hire: false }));
    }
  };

  // Function to view test results
  const handleViewResult = async (testType, candidate) => {
    if (!testType || !candidate) {
      toast.error('Invalid test or candidate data');
      return;
    }
    
    try {
      const companyEmail = getCompanyEmail();
      const companyId = getCompanyId();
      
      if (!companyEmail || !companyId) {
        toast.error('Missing company credentials. Please log in again.');
        return;
      }

      // For overall results, fetch test details and navigate to reports page
      if (testType === 'overall') {
        if (!candidate.jobId || candidate.jobId === 'unknown') {
          toast.error('Unable to determine job ID for this candidate. Please try again.');
          return;
        }

        // Fetch test details using the candidate's email, job ID, and test IDs
        const requestBody = {
          candidate_id:          candidate.id,
          job_id:                candidate.jobId,
          screening_test_id:     candidate.screening_test_id     || null,
          personality_test_id:   candidate.personality_test_id   || null,
          behavioral_test_id:    candidate.behavioral_test_id    || null,
          coding_test_id:        candidate.coding_test_id        || null,
          communication_test_id: candidate.communication_test_id || null,
        };

        const response = await fetch(getApiUrl('/api/view_test_details_candidate'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
          let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
          try {
            const errorData = await response.json();
            errorMessage = errorData.error || errorData.message || errorMessage;
          } catch (e) { /* ignore */ }
          toast.error(`Failed to fetch test results: ${errorMessage}`);
          return;
        }

        const testData = await response.json();
        const result = testData.candidate_result || (testData.results && testData.results[0]) || null;

        if (result) {
          const scores = [
            {
              label: 'Screening',
              score: result.screening_score != null
                ? `${Math.round(result.screening_score)}/100`
                : candidate.screening_scheduled_time ? 'Scheduled' : 'Not Scheduled',
            },
            {
              label: 'Personality',
              score: result.personality_score != null
                ? `${Math.round(result.personality_score)}/100`
                : candidate.personality_scheduled_time ? 'Scheduled' : 'Not Scheduled',
            },
            {
              label: 'Behavioural',
              score: result.behavioral_score != null
                ? `${Math.round(result.behavioral_score)}/100`
                : candidate.behavioral_scheduled_time ? 'Scheduled' : 'Not Scheduled',
            },
            {
              label: 'Coding',
              score: result.coding_score != null
                ? `${Math.round(result.coding_score)}/100`
                : candidate.coding_scheduled_time ? 'Scheduled' : 'Not Scheduled',
            },
            {
              label: 'Communication',
              score: result.communication_score != null
                ? `${Math.round(result.communication_score)}/100`
                : candidate.communication_scheduled_time ? 'Scheduled' : 'Not Scheduled',
            },
          ];

          navigate(`/reports?jobID=${candidate.jobId}`, {
            state: {
              candidate: {
                details:       { ...candidate, ...result },
                scores,
                rank:          result.rank             || 0,
                total_results: result.total_candidates || (testData.results?.length) || 0,
                tests: {
                  screening:     result.screening_details     || null,
                  coding:        result.coding_details        || null,
                  personality:   result.personality_details   || null,
                  behavioral:    result.behavioral_details    || null,
                  communication: result.communication_details || null,
                },
              },
              jobDetails: testData.job_details || {},
            },
          });
        } else {
          toast.error('Unable to fetch test results. Please try again.');
        }
        return;
      }

      // For individual test types, open test results in a new tab
      const testUrl = getApiUrl(`/api/view_test_results?candidate_email=${encodeURIComponent(candidate.id)}&test_type=${testType}`);
      window.open(testUrl, '_blank');
    } catch (error) {
      console.error('Error viewing test results:', error);
      toast.error('Error viewing test results. Please try again.');
    }
  };

  // Load initial data
  useEffect(() => {
    fetchJobTitles();
  }, []); // Remove fetchJobTitles from dependency to prevent infinite loops

  useEffect(() => {
    if (jobs.length > 0 && selectedJobId) {
      fetchCandidates(selectedJobId, page);
    }
  }, [selectedJobId, page, fetchCandidates, jobs.length]); // Include jobs.length in dependency

  const onSelectJob = useCallback((jid) => { 
    // Convert to string to ensure consistency
    const jobId = jid ? jid.toString() : jid;
    
    setSelectedJobId(jobId); 
    setJobFilter(jobId); 
    setPage(1); 
  }, []);

  // Handle job filter change - now handled inline in Select component

  const appsAll = applications;
  const appsForJob = useMemo(() => {
    let pool;
    if (selectedJobId === "all" || !selectedJobId) {
      pool = appsAll;
    } else {
      pool = appsAll.filter(a => a.jobId.toString() === selectedJobId.toString());
    }
    
    // Only filter by scheduled tests when a specific job is selected (not for "All Jobs")
    if (selectedJobId !== "all" && selectedJobId) {
      pool = pool.filter(a => a.hasScheduledTests);
    }
    
    const filteredPool = pool.filter(a => {
      // Search filter by candidate name
      if (searchQuery && searchQuery.trim() !== "") {
        const query = searchQuery.toLowerCase().trim();
        const candidateName = (a.candidate.name || "").toLowerCase();
        const candidateEmail = (a.candidate.id || "").toLowerCase();
        const candidateLocation = (a.candidate.location || "").toLowerCase();
        
        // Search in name, email, or location
        if (!candidateName.includes(query) && 
            !candidateEmail.includes(query) && 
            !candidateLocation.includes(query)) {
          return false;
        }
      }

      // Hired filter
      if (hiredFilter === "hired" && !a.isHired) return false;
      if (hiredFilter === "not_hired" && a.isHired) return false;
      
      if (testFilter !== "all") {
        const t = a.tests[testFilter];
        if (!t) return false;
        if (statusFilter !== "all" && t.status !== statusFilter) return false;
        return true;
      }
      if (statusFilter !== "all") {
        return TESTS.some(t => a.tests[t]?.status === statusFilter);
      }
      return true;
    });

    // Sort candidates by overall score (highest first) - no rank numbers shown
    return filteredPool.sort((a, b) => {
      const scoreA = weightedOverall(a.tests);
      const scoreB = weightedOverall(b.tests);
      
      // Handle null scores - candidates with scores come first
      if (scoreA === null && scoreB === null) return 0;
      if (scoreA === null) return 1;
      if (scoreB === null) return -1;
      
      // Sort by score descending (highest first)
      return scoreB - scoreA;
    });
  }, [appsAll, selectedJobId, testFilter, statusFilter, searchQuery, hiredFilter]);

  /* ----------------------------- Calendar view ----------------------------- */
  // Flatten every scheduled test session (across the candidates that pass the
  // active job/search/status filters) into individual dated events. A single
  // candidate can contribute up to 4 events (one per test type).
  const calendarEvents = useMemo(() => {
    const evs = [];
    for (const a of appsForJob) {
      for (const t of TESTS) {
        const tt = a.tests?.[t];
        if (!tt?.scheduledAt) continue;
        const d = new Date(tt.scheduledAt);
        if (isNaN(d.getTime())) continue;
        evs.push({
          key: `${a.id}-${t}`,
          app: a,
          test: t,
          status: tt.status,
          score: tt.score,
          date: d,
          dayKey: calDayKey(d),
          minutes: d.getHours() * 60 + d.getMinutes(),
        });
      }
    }
    // Chronological within a day so the earliest session sits at the top.
    evs.sort((x, y) => x.date - y.date);
    return evs;
  }, [appsForJob]);

  const eventsByDay = useMemo(() => {
    const m = new Map();
    for (const e of calendarEvents) {
      if (!m.has(e.dayKey)) m.set(e.dayKey, []);
      m.get(e.dayKey).push(e);
    }
    return m;
  }, [calendarEvents]);

  // Weeks (arrays of 7 day-cells) covering the visible month or the single week.
  const calWeeks = useMemo(() => {
    const cursor = calCursor;
    let start;
    let end;
    if (calMode === "week") {
      start = new Date(cursor);
      start.setDate(cursor.getDate() - cursor.getDay()); // back to Sunday
      end = new Date(start);
      end.setDate(start.getDate() + 6);
    } else {
      const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
      start = new Date(first);
      start.setDate(first.getDate() - first.getDay()); // Sunday on/before the 1st
      const last = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
      end = new Date(last);
      end.setDate(last.getDate() + (6 - last.getDay())); // Saturday on/after the last
    }
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    const weeks = [];
    const walker = new Date(start);
    while (walker <= end) {
      const week = [];
      for (let i = 0; i < 7; i++) {
        week.push({
          date: new Date(walker),
          dayKey: calDayKey(walker),
          inMonth: walker.getMonth() === cursor.getMonth(),
        });
        walker.setDate(walker.getDate() + 1);
      }
      weeks.push(week);
    }
    return weeks;
  }, [calCursor, calMode]);

  const calTitle = useMemo(() => {
    if (calMode === "week") {
      const week = calWeeks[0] || [];
      const s = week[0]?.date;
      const e = week[6]?.date;
      if (!s || !e) return "";
      const sM = s.toLocaleDateString("en-US", { month: "short" });
      const eM = e.toLocaleDateString("en-US", { month: "short" });
      if (s.getMonth() === e.getMonth()) {
        return `${sM} ${s.getDate()} – ${e.getDate()}, ${e.getFullYear()}`;
      }
      return `${sM} ${s.getDate()} – ${eM} ${e.getDate()}, ${e.getFullYear()}`;
    }
    return `${CAL_MONTHS[calCursor.getMonth()]} ${calCursor.getFullYear()}`;
  }, [calMode, calCursor, calWeeks]);

  const calVisibleCount = useMemo(() => {
    let n = 0;
    for (const week of calWeeks) {
      for (const day of week) n += eventsByDay.get(day.dayKey)?.length || 0;
    }
    return n;
  }, [calWeeks, eventsByDay]);

  const todayKey = calDayKey(new Date());

  const calShift = useCallback((dir) => {
    setCalCursor((prev) => {
      const d = new Date(prev);
      if (calMode === "week") d.setDate(d.getDate() + dir * 7);
      else d.setMonth(d.getMonth() + dir);
      return d;
    });
  }, [calMode]);

  const calGoToday = useCallback(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    setCalCursor(d);
  }, []);

  // KPIs from all apps (global)
  const kpis = useMemo(() => {
    let scheduled = 0, completed = 0, overdue = 0, hired = 0;
    for (const a of appsAll) {
      // Count hired candidates
      if (a.isHired) hired++;
      
      for (const t of TESTS) {
        const s = a.tests[t]?.status;
        if (s === "scheduled") scheduled++;
        if (s === "completed") completed++;
        if (s === "overdue") overdue++;
      }
    }
    return { scheduled, completed, overdue, hired };
  }, [appsAll]);

  // Use totalCandidates from API for proper pagination
  const total = totalCandidates;
  const rows = appsForJob; // Show all fetched candidates for current page

  if (jobsLoading) {
    return <SkeletonInterviewPage />;
  }

  if (jobs.length === 0) {
    return (
      <div className="flex flex-col w-full">
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16 px-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted mb-4">
              <FileText className="h-8 w-8 text-muted-foreground" />
        </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              No jobs found
            </h3>
            <p className="text-sm text-muted-foreground text-center">
              {jobsError || 'Please create some job postings first.'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full gap-6">
      {/* header */}
      <div className="flex justify-between items-start">
        <Heading level="h4">Scheduled Interviews</Heading>
      </div>

        {/* filters */}
        <Card>
          <CardContent className="p-3">
            <div className="space-y-3">
              {/* First Row - Search and Main Filters */}
              <div className="flex flex-wrap items-end gap-3">
                {/* Search */}
                <div className="flex-1 min-w-[250px]">
                  <Label htmlFor="search-filter" className="text-xs mb-1.5 block">Search</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="search-filter"
                      type="text"
                      placeholder="Search candidate by name, email, or location..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="h-9 text-sm pl-10 pr-10"
                    />
                    {searchQuery.trim() && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                        onClick={() => setSearchQuery('')}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>

                {/* Job Filter */}
                <div className="w-[180px]">
                  <Label htmlFor="job-filter" className="text-xs mb-1.5 block">Job</Label>
                  <Select value={jobFilter} onValueChange={(value) => {
                    const jobId = value === "all" ? "all" : value.toString();
                    setJobFilter(jobId);
                    setSelectedJobId(jobId);
                    setPage(1);
                    if (jobId && jobs.length > 0) {
                      fetchCandidates(jobId, 1);
                    }
                  }}>
                    <SelectTrigger id="job-filter" className="h-9 text-sm">
                      <SelectValue placeholder="Select job" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Jobs</SelectItem>
                      {jobs.map(j => (
                        <SelectItem key={j.id} value={j.id.toString()}>{j.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Test Type Filter */}
                <div className="w-[160px]">
                  <Label htmlFor="test-filter" className="text-xs mb-1.5 block">Test Type</Label>
                  <Select value={testFilter} onValueChange={(value) => { setTestFilter(value); setPage(1); }}>
                    <SelectTrigger id="test-filter" className="h-9 text-sm">
                      <SelectValue placeholder="Test type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Test Types</SelectItem>
                      <SelectItem value="screening">Screening</SelectItem>
                      <SelectItem value="personality">Personality</SelectItem>
                      <SelectItem value="behavioral">Behavioral</SelectItem>
                      <SelectItem value="coding">Coding</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Status Filter */}
                <div className="w-[160px]">
                  <Label htmlFor="status-filter" className="text-xs mb-1.5 block">Status</Label>
                  <Select value={statusFilter} onValueChange={(value) => { setStatusFilter(value); setPage(1); }}>
                    <SelectTrigger id="status-filter" className="h-9 text-sm">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="not_scheduled">Not scheduled</SelectItem>
                      <SelectItem value="scheduled">Scheduled</SelectItem>
                      <SelectItem value="in_progress">In progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="overdue">Overdue</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Hired Status Filter */}
                <div className="w-[140px]">
                  <Label htmlFor="hired-filter" className="text-xs mb-1.5 block">Hired Status</Label>
                  <Select value={hiredFilter} onValueChange={(value) => { setHiredFilter(value); setPage(1); }}>
                    <SelectTrigger id="hired-filter" className="h-9 text-sm">
                      <SelectValue placeholder="Hired status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Candidates</SelectItem>
                      <SelectItem value="hired">Hired Only</SelectItem>
                      <SelectItem value="not_hired">Not Hired</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Page Size */}
                <div className="w-[140px]">
                  <Label htmlFor="page-size" className="text-xs mb-1.5 block">Page Size</Label>
                  <Select value={pageSize.toString()} onValueChange={(value) => { setPageSize(Number(value)); setPage(1); }}>
                    <SelectTrigger id="page-size" className="h-9 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="100">100 per page</SelectItem>
                      <SelectItem value="200">200 per page</SelectItem>
                      <SelectItem value="500">500 per page</SelectItem>
                      <SelectItem value="1000">1000 per page</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Clear Button */}
                {(jobFilter !== "all" || testFilter !== "all" || statusFilter !== "all" || hiredFilter !== "all" || searchQuery.trim()) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setJobFilter("all");
                      setTestFilter("all");
                      setStatusFilter("all");
                      setHiredFilter("all");
                      setSearchQuery("");
                      setPage(1);
                    }}
                    className="h-9 text-xs"
                  >
                    <X className="w-3 h-3 mr-1" />
                    Clear All
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Active Filters Display */}
        {(jobFilter !== "all" || testFilter !== "all" || statusFilter !== "all" || hiredFilter !== "all" || searchQuery.trim()) && (
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-xs text-muted-foreground font-medium">Active filters:</span>
            {searchQuery.trim() && (
              <Badge variant="secondary" className="gap-1.5">
                Search: "{searchQuery}"
                <button
                  onClick={() => setSearchQuery('')}
                  className="ml-1 rounded-full hover:bg-muted-foreground/20 p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )}
            {jobFilter !== "all" && (
              <Badge variant="secondary" className="gap-1.5">
                Job: {jobs.find(j => j.id.toString() === jobFilter?.toString())?.title || jobFilter}
                <button
                  onClick={() => {
                    setJobFilter("all");
                    setSelectedJobId("all");
                    setPage(1);
                    if (jobs.length > 0) {
                      fetchCandidates("all", 1);
                    }
                  }}
                  className="ml-1 rounded-full hover:bg-muted-foreground/20 p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )}
            {testFilter !== "all" && (
              <Badge variant="secondary" className="gap-1.5">
                Test: {testFilter.charAt(0).toUpperCase() + testFilter.slice(1)}
                <button
                  onClick={() => { setTestFilter("all"); setPage(1); }}
                  className="ml-1 rounded-full hover:bg-muted-foreground/20 p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )}
            {statusFilter !== "all" && (
              <Badge variant="secondary" className="gap-1.5">
                Status: {statusFilter.replace('_', ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                <button
                  onClick={() => { setStatusFilter("all"); setPage(1); }}
                  className="ml-1 rounded-full hover:bg-muted-foreground/20 p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )}
            {hiredFilter !== "all" && (
              <Badge variant="secondary" className="gap-1.5">
                Hired: {hiredFilter === "hired" ? "Yes" : "No"}
                <button
                  onClick={() => { setHiredFilter("all"); setPage(1); }}
                  className="ml-1 rounded-full hover:bg-muted-foreground/20 p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )}
          </div>
        )}

        {/* KPIs */}
        {loading && jobs.length === 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            {[1, 2, 3, 4].map(i => (
              <SkeletonKPI key={i} />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <KPI label="Scheduled (all tests)" value={kpis.scheduled} type="scheduled" />
            <KPI label="Completed (all tests)" value={kpis.completed} type="completed" />
            <KPI label="Overdue" value={kpis.overdue} type="overdue" />
            <KPI label="🎉 Hired Candidates" value={kpis.hired} type="hired" />
          </div>
        )}

        {/* job picker */}
        <ScrollArea className="w-full pb-2 mb-4">
          <div className="flex gap-2 w-max pb-4">
            {loading && jobs.length === 0 ? (
              <>
                {[1, 2, 3, 4].map(i => (
                  <SkeletonJobButton key={i} />
                ))}
              </>
            ) : (
              <>
                {jobs.map(job => {
                  const count = appsAll.filter(a => a.jobId.toString() === job.id.toString()).length;
                  const active = job.id.toString() === selectedJobId?.toString();
                  return (
                    <Button
                      key={job.id}
                      variant={active ? "default" : "outline"}
                      size="sm"
                      className={cn(
                        "h-auto py-2 px-4 text-left justify-start flex-shrink-0 whitespace-nowrap",
                        active && "ring-2 ring-primary"
                      )}
                      style={active ? {
                        backgroundColor: getColor('primary'),
                        color: getColor('primaryForeground'),
                        borderColor: getColor('primary')
                      } : undefined}
                      onClick={() => onSelectJob(job.id)}
                    >
                      <div className="flex flex-col gap-1">
                        <span className="font-semibold text-sm">{job.title}</span>
                        <span className={cn(
                          "text-xs",
                          active ? "text-primary-foreground/80" : "text-muted-foreground"
                        )}>
                          <MapPin className="h-3 w-3 inline mr-1" />
                          {job.location}
                        </span>
                      </div>
                    </Button>
                  );
                })}
              </>
            )}
          </div>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>

        {/* candidate panel */}
        <Card>
          <Tabs value={view} onValueChange={setView} className="w-full">
            <CardHeader>
              <div className="flex justify-between items-center flex-wrap gap-4">
                <div className="flex-1 min-w-0">
                  <CardTitle>
              {jobs.find(j => j.id.toString() === selectedJobId?.toString())?.title || "All Jobs"}
                  </CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    {loading && rows.length === 0 ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Loading candidates...
              </span>
                    ) : (
                      <>
                        {total > 0 ? (
                          <>
                            Showing <span className="font-semibold text-foreground">{rows.length}</span> of <span className="font-semibold text-foreground">{total}</span> candidate{total !== 1 ? 's' : ''}
                            {searchQuery && ` matching "${searchQuery}"`}
                            {loading && rows.length > 0 && (
                              <span className="ml-2 inline-flex items-center gap-2 text-xs text-muted-foreground">
                                <Loader2 className="h-3 w-3 animate-spin" />
                                Refreshing…
                              </span>
                            )}
                          </>
                        ) : (
                          "No candidates found"
                        )}
                      </>
                    )}
                  </p>
            </div>
                <TabsList>
                  <TabsTrigger value="list" className="gap-2">
                    <List className="h-4 w-4" />
                    List
                  </TabsTrigger>
                  <TabsTrigger value="kanban" className="gap-2">
                    <LayoutGrid className="h-4 w-4" />
                    Kanban
                  </TabsTrigger>
                  <TabsTrigger value="calendar" className="gap-2">
                    <CalendarIcon className="h-4 w-4" />
                    Calendar
                  </TabsTrigger>
                </TabsList>
          </div>
            </CardHeader>
            <CardContent>
              <TabsContent value="list">
              {loading && rows.length === 0 ? (
                <div className="overflow-x-auto pb-1">
                <div className="min-w-[1100px] space-y-4">
                  {/* Table header skeleton */}
                  <div className="border-b pb-2">
                    <div className={cn(CANDIDATE_TABLE_GRID, "px-2")}>
                      <Skeleton className="h-4 w-20" />
                      <Skeleton className="h-4 w-16" />
                      <Skeleton className="h-4 w-12 justify-self-center" />
                      <Skeleton className="h-4 w-32 justify-self-end" />
                  </div>
                  </div>
                  {/* Table rows skeleton */}
                  <div className="space-y-2">
                    {[1, 2, 3, 4, 5, 6].map(i => (
                    <SkeletonCandidateRow key={i} />
                  ))}
                  </div>
                </div>
                </div>
              ) : rows.length === 0 ? (
                <Card className="border-dashed">
                  <CardContent className="flex flex-col items-center justify-center py-12 px-4">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted mb-4">
                      <Users className="h-8 w-8 text-muted-foreground" />
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">
                      No candidates found
                    </h3>
                    <p className="text-sm text-muted-foreground text-center mb-4">
                  {selectedJobId === "all" 
                    ? "No candidates found across all jobs" 
                    : `No candidates found for ${jobs.find(j => j.id.toString() === selectedJobId?.toString())?.title || "this job"}`
                  }
                    </p>
                    {(jobFilter !== "all" || testFilter !== "all" || statusFilter !== "all" || hiredFilter !== "all" || searchQuery.trim()) && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setJobFilter("all");
                          setTestFilter("all");
                          setStatusFilter("all");
                          setHiredFilter("all");
                          setSearchQuery("");
                          setPage(1);
                        }}
                      >
                        Clear all filters
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <>
                  <div className="overflow-x-auto pb-1">
                    <div className="min-w-[1100px]">
                  <div className="border-b mb-3 pb-2">
                    <div className={cn(CANDIDATE_TABLE_GRID, "px-3 text-sm font-semibold text-muted-foreground")}>
                    <div>Candidate</div>
                    <div>Tests</div>
                      <div className="text-center">Overall</div>
                      <div className="text-right">Actions</div>
                  </div>
                  </div>
                  <div className="space-y-1">
                  {rows.map(a => (
                    <div key={a.id} className={cn(CANDIDATE_TABLE_GRID, "items-start rounded-lg border-t border-border p-3 first:border-t-0 hover:bg-muted/50 transition-colors")}>
                      <div className="flex min-w-0 items-start gap-3">
                        <Avatar className="-mt-1 h-10 w-10">
                          <AvatarFallback className="bg-muted text-muted-foreground">
                            {a.candidate.name?.charAt(0)?.toUpperCase() || '👤'}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0 space-y-1.5">
                          <div className={cn(
                            "flex min-w-0 items-center gap-2 font-semibold text-foreground",
                            a.isHired && "text-green-600"
                          )}>
                            <span className="block min-w-0 truncate">{displayCandidateName(a.candidate.name)}</span>
                            <HiringPipelineStatusBadge
                              status={a.lifecycleStatus}
                              isHired={a.isHired}
                              className="shrink-0"
                            />
                          </div>
                          <div className="text-sm text-muted-foreground">
                            <MapPin className="h-3 w-3 inline mr-1" />
                            {a.candidate.location} • Applied {a.appliedAt}
                            {a.isHired && a.hiredAt && (
                              <> • Hired on {new Date(a.hiredAt).toISOString().split('T')[0]}</>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center flex-nowrap gap-2 whitespace-nowrap pt-0.5">
                        {TESTS.map(t => (
                          <Chip key={t} label={t.charAt(0).toUpperCase()+t.slice(1)} data={a.tests[t]} />
                        ))}
                      </div>
                      <div className="flex items-center justify-center pt-0.5">
                        {weightedOverall(a.tests) !== null
                          ? <Badge className="bg-foreground text-background">{weightedOverall(a.tests)}%</Badge>
                          : <Text size="sm" variant="muted">N/A</Text>}
                      </div>
                      <div className="flex items-center justify-end gap-2 whitespace-nowrap pt-0.5">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setDrawer(a)}
                        >
                          Manage
                        </Button>
                        <Button 
                          variant="default"
                          size="sm"
                          onClick={() => {
                            const candidateWithJobId = { ...a.candidate, jobId: a.jobId };
                            handleViewResult('overall', candidateWithJobId);
                          }}
                          disabled={!a.candidate || weightedOverall(a.tests) === null}
                          style={!a.candidate || weightedOverall(a.tests) === null ? undefined : {
                            backgroundColor: getColor('primary'),
                            color: getColor('primaryForeground'),
                            borderColor: getColor('primary')
                          }}
                        >
                          Results
                        </Button>
                        
                      </div>
                    </div>
                  ))}
                  </div>
                    </div>
                  </div>
                </>
              )}
              </TabsContent>

              <TabsContent value="kanban">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3 p-4">
              {[
                ["not_scheduled", "Not Scheduled"],
                ["scheduled", "Scheduled"],
                ["in_progress", "In Progress"],
                ["completed", "Completed"],
                ["overdue", "Overdue"],
              ].map(([key, label]) => (
                <Card key={key} className="min-h-[320px]">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base font-medium">{label}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                  {appsAll
                    .filter(a => a.jobId.toString() === selectedJobId?.toString() || selectedJobId === "all")
                    .filter(a => TESTS.some(t => a.tests[t]?.status === key))
                    .map(a => (
                      <Card key={a.id} className="cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => setDrawer(a)}>
                        <CardContent className="p-3">
                          <div className="flex items-center gap-2 flex-wrap mb-2">
                            <Text className={cn("font-medium", a.isHired && "text-green-600 font-semibold")}>
                              {a.candidate.name}
                            </Text>
                            <HiringPipelineStatusBadge
                              status={a.lifecycleStatus}
                              isHired={a.isHired}
                              className="text-[10px] px-2.5 py-0.5 uppercase tracking-wide"
                            />
                          </div>
                          <Text size="sm" variant="muted" className="mb-2">
                            {jobs.find(j => j.id.toString() === a.jobId?.toString())?.title || "Unknown Job"}
                          </Text>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {TESTS.map(t => <Chip key={t} label={t} data={a.tests[t]} />)}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </CardContent>
                </Card>
              ))}
            </div>
              </TabsContent>

              <TabsContent value="calendar">
                {/* Toolbar: navigation + title + month/week switch */}
                <div className="flex flex-col gap-3 border-b pb-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center rounded-md border bg-background">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 rounded-r-none"
                        onClick={() => calShift(-1)}
                        aria-label={calMode === "week" ? "Previous week" : "Previous month"}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 rounded-none border-x px-3 text-xs font-medium"
                        onClick={calGoToday}
                      >
                        Today
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 rounded-l-none"
                        onClick={() => calShift(1)}
                        aria-label={calMode === "week" ? "Next week" : "Next month"}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                    <h3 className="text-base font-semibold text-foreground sm:text-lg">
                      {calTitle}
                    </h3>
                  </div>

                  <div className="flex items-center gap-1 rounded-md border bg-background p-0.5">
                    {["month", "week"].map((m) => {
                      const active = calMode === m;
                      return (
                        <button
                          key={m}
                          type="button"
                          onClick={() => setCalMode(m)}
                          className={cn(
                            "rounded px-3 py-1 text-xs font-medium capitalize transition-colors",
                            active
                              ? "text-white"
                              : "text-muted-foreground hover:text-foreground"
                          )}
                          style={active ? { backgroundColor: getColor("primary") } : undefined}
                        >
                          {m}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Count summary + colour legend */}
                <div className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-sm text-muted-foreground">
                    {calVisibleCount > 0 ? (
                      <>
                        <span className="font-semibold text-foreground">{calVisibleCount}</span>{" "}
                        session{calVisibleCount !== 1 ? "s" : ""} scheduled in {calTitle}
                      </>
                    ) : (
                      <>No sessions scheduled in {calTitle}</>
                    )}
                  </p>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    {TESTS.map((t) => (
                      <span key={t} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                        <span className={cn("h-2 w-2 rounded-full", TEST_META[t]?.dot)} />
                        {TEST_META[t]?.label}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Grid */}
                <div className="overflow-x-auto pb-1">
                  <div className="min-w-[760px]">
                    {/* Weekday header */}
                    <div className="grid grid-cols-7 border-b">
                      {CAL_WEEKDAYS.map((wd) => (
                        <div
                          key={wd}
                          className="px-2 py-2 text-center text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                        >
                          {wd}
                        </div>
                      ))}
                    </div>

                    {/* Weeks */}
                    <div className={cn("grid", calMode === "week" ? "grid-rows-1" : "")}>
                      {calWeeks.map((week, wi) => (
                        <div key={wi} className="grid grid-cols-7">
                          {week.map((day) => {
                            const dayEvents = eventsByDay.get(day.dayKey) || [];
                            const dim = calMode === "month" && !day.inMonth;
                            const isToday = day.dayKey === todayKey;
                            const maxPills = calMode === "week" ? dayEvents.length : 3;
                            const shown = dayEvents.slice(0, maxPills);
                            const overflow = dayEvents.length - shown.length;
                            return (
                              <div
                                key={day.dayKey}
                                className={cn(
                                  "flex flex-col gap-1 border-b border-r p-1.5 [&:nth-child(7n)]:border-r-0",
                                  calMode === "week" ? "min-h-[460px]" : "min-h-[116px]",
                                  dim ? "bg-muted/30" : "bg-background",
                                  isToday && "bg-blue-50/60 dark:bg-blue-950/20"
                                )}
                              >
                                <div className="flex items-center justify-between">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      dayEvents.length > 0 &&
                                      setCalDay({ date: day.date, events: dayEvents })
                                    }
                                    className={cn(
                                      "inline-flex h-6 min-w-[1.5rem] items-center justify-center rounded-full px-1 text-xs font-semibold transition-colors",
                                      isToday && "text-white",
                                      !isToday && dim && "text-muted-foreground",
                                      !isToday && !dim && "text-foreground",
                                      dayEvents.length > 0 && !isToday && "hover:bg-muted"
                                    )}
                                    style={isToday ? { backgroundColor: getColor("primary") } : undefined}
                                  >
                                    {day.date.getDate()}
                                  </button>
                                  {dayEvents.length > 0 && (
                                    <span className="text-[10px] font-medium text-muted-foreground">
                                      {dayEvents.length}
                                    </span>
                                  )}
                                </div>

                                <div className={cn("flex flex-col gap-1", calMode === "week" && "overflow-y-auto")}>
                                  {shown.map((ev) => (
                                    <CalendarEventPill
                                      key={ev.key}
                                      event={ev}
                                      onOpen={(e) => setDrawer(e.app)}
                                    />
                                  ))}
                                  {overflow > 0 && (
                                    <button
                                      type="button"
                                      onClick={() => setCalDay({ date: day.date, events: dayEvents })}
                                      className="rounded-md px-1.5 py-0.5 text-left text-[11px] font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
                                    >
                                      +{overflow} more
                                    </button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </TabsContent>
            </CardContent>
          </Tabs>
        </Card>

      <OfferModal
        open={offerModalOpen}
        candidate={offerCandidate}
        onClose={() => { setOfferModalOpen(false); setOfferCandidate(null); }}
      />

      {/* Calendar day-detail dialog: every session on the clicked day */}
      <Dialog open={!!calDay} onOpenChange={(open) => !open && setCalDay(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <CalendarIcon className="h-5 w-5 text-muted-foreground" />
              {calDay?.date?.toLocaleDateString("en-US", {
                weekday: "long", month: "long", day: "numeric", year: "numeric",
              })}
            </DialogTitle>
            <DialogDescription>
              {calDay?.events?.length || 0} scheduled session
              {(calDay?.events?.length || 0) !== 1 ? "s" : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] space-y-2 overflow-y-auto pr-1">
            {(calDay?.events || []).map((ev) => {
              const meta = TEST_META[ev.test] || {};
              const isCompleted = ev.status === "completed";
              const isOverdue = ev.status === "overdue";
              return (
                <button
                  key={ev.key}
                  type="button"
                  onClick={() => { setDrawer(ev.app); setCalDay(null); }}
                  className="flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors hover:bg-muted/50"
                >
                  <Avatar className="h-9 w-9 shrink-0">
                    <AvatarFallback className="bg-muted text-muted-foreground">
                      {ev.app?.candidate?.name?.charAt(0)?.toUpperCase() || "👤"}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium text-foreground">
                      {displayCandidateName(ev.app?.candidate?.name)}
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <span className={cn("h-2 w-2 rounded-full", meta.dot)} />
                      {meta.label || ev.test}
                      <span>·</span>
                      <Clock className="h-3 w-3" />
                      {formatTimeOnly(ev.date)}
                    </div>
                  </div>
                  {isCompleted ? (
                    <Badge className="shrink-0 border-green-200 bg-green-100 text-green-800">Completed</Badge>
                  ) : isOverdue ? (
                    <Badge variant="destructive" className="shrink-0">Overdue</Badge>
                  ) : (
                    <Badge className="shrink-0 border-blue-200 bg-blue-100 text-blue-800">Scheduled</Badge>
                  )}
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* Candidate Management Dialog */}
      <Dialog open={!!drawer} onOpenChange={(open) => !open && setDrawer(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {drawer && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-4">
                  <Avatar className="h-16 w-16">
                    <AvatarFallback className="bg-muted text-muted-foreground text-lg">
                      {drawer.candidate.name?.charAt(0)?.toUpperCase() || '👤'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <DialogTitle className={cn(
                      "text-2xl flex items-center gap-2",
                      drawer.isHired && "text-green-600"
                    )}>
                      {drawer.candidate.name}
                      <HiringPipelineStatusBadge
                        status={drawer.lifecycleStatus}
                        isHired={drawer.isHired}
                        className="ml-2"
                      />
                    </DialogTitle>
                    <DialogDescription className="text-base mt-1">
                      <div className="flex items-center gap-4 flex-wrap">
                        <span className="flex items-center gap-1">
                          <MapPin className="h-4 w-4" />
                          {drawer.candidate.location}
                        </span>
                        <span className="text-muted-foreground">
                          {jobs.find(j => j.id.toString() === drawer.jobId?.toString())?.title || "Unknown Job"}
                        </span>
                        {drawer.hiredAt && (
                          <span className="text-sm text-green-600">
                            Hired on {new Date(drawer.hiredAt).toISOString().split('T')[0]}
                          </span>
                        )}
                      </div>
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>

              <Separator className="my-0" />

              <div className="space-y-2">
                {/* Overview Section - Optimized Layout */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 p-3 bg-muted/30 rounded-lg border">
                  {/* Overall Score - Inline */}
                  <div className="flex items-center gap-3">
                    <div>
                      <div className="text-xs text-muted-foreground mb-0.5">Overall Score</div>
                      <div className="flex items-baseline gap-1">
                        <div className="text-2xl font-bold text-foreground">
                          {weightedOverall(drawer.tests) ?? "N/A"}
                        </div>
                        {weightedOverall(drawer.tests) !== null && (
                          <span className="text-base text-muted-foreground">%</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Quick Actions - Inline */}
                  <div className="flex flex-wrap gap-2 ml-auto">
                    <Button 
                      variant="default"
                      size="sm"
                      onClick={() => {
                        const candidateWithJobId = { ...drawer.candidate, jobId: drawer.jobId };
                        handleViewResult('overall', candidateWithJobId);
                      }}
                      disabled={!drawer.candidate || weightedOverall(drawer.tests) === null}
                      style={!drawer.candidate || weightedOverall(drawer.tests) === null ? undefined : {
                        backgroundColor: getColor('primary'),
                        color: getColor('primaryForeground'),
                        borderColor: getColor('primary')
                      }}
                    >
                      <BarChart3 className="h-4 w-4 mr-1.5" />
                      View Results
                    </Button>
                    
                    {can('onboarding.manage') && !drawer.isHired && !getHiringPipelineStatus(drawer.lifecycleStatus, drawer.isHired) && (
                      <Button 
                        variant="default"
                        size="sm"
                        className="bg-green-600 hover:bg-green-700 text-white"
                        onClick={() => handleMarkAsHired(drawer.candidate, drawer.jobId)}
                        disabled={actionLoading.hire || !drawer.candidate || weightedOverall(drawer.tests) === null}
                      >
                        {actionLoading.hire ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                            Processing...
                          </>
                        ) : (
                          <>
                            <CheckCircle2 className="h-4 w-4 mr-1.5" />
                            Mark as Selected
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>

                <Separator className="" />

                {/* Tests Section */}
                <div>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <FileCheck className="h-5 w-5" />
                    Test Management
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {TESTS.map(t => {
                      const test = drawer.tests[t];
                      const status = test?.status || "not_scheduled";
                      const locked = tokenLocked(ent, t);
                      return (
                        <Card key={t} className={cn("border", locked && "border-destructive/30 bg-destructive/[0.03]")}>
                          <CardHeader className="pb-3">
                            <div className="flex items-center justify-between">
                              <CardTitle className="text-base capitalize flex items-center gap-1.5">
                                {t}
                                {locked && <Lock className="h-3.5 w-3.5 text-destructive" title={`Not in ${ent?.plan_name || 'your'} plan`} />}
                              </CardTitle>
                              <Chip
                                label={t.charAt(0).toUpperCase() + t.slice(1)}
                                data={test}
                              />
                            </div>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <Label className="text-xs text-muted-foreground">Status</Label>
                                <div className="text-sm font-medium mt-0.5 capitalize">
                                  {status.replace('_', ' ')}
                                </div>
                              </div>
                              <div>
                                <Label className="text-xs text-muted-foreground">Score</Label>
                                <div className="text-sm font-medium mt-0.5">
                                  {test?.score ?? "—"}
                                  {test?.score !== null && test?.score !== undefined && "%"}
                                </div>
                              </div>
                              <div>
                                <Label className="text-xs text-muted-foreground">Scheduled</Label>
                                <div className="text-xs font-medium mt-0.5">
                                  {test?.scheduledAt ? formatDateTime(test.scheduledAt) : "—"}
                                </div>
                              </div>
                              <div>
                                <Label className="text-xs text-muted-foreground">Completed</Label>
                                <div className="text-xs font-medium mt-0.5">
                                  {test?.completedAt ? new Date(test.completedAt).toLocaleDateString() : "—"}
                                </div>
                              </div>
                            </div>
                            
                            <Separator />
                            
                            <div className="flex gap-2">
                              {status === "not_scheduled" ? (
                                can('tests.schedule') && (
                                <Button
                                  variant={locked ? "outline" : "default"}
                                  size="sm"
                                  className="flex-1"
                                  onClick={() => handleScheduleTest(t, drawer.candidate)}
                                  disabled={actionLoading.reschedule || !drawer.candidate || locked}
                                  title={locked ? `Not included in your ${ent?.plan_name || 'current'} plan` : undefined}
                                  style={locked ? undefined : {
                                    backgroundColor: getColor('primary'),
                                    color: getColor('primaryForeground'),
                                    borderColor: getColor('primary')
                                  }}
                                >
                                  {locked ? (
                                    <>
                                      <Lock className="h-3 w-3 mr-1.5" />
                                      Not in plan
                                    </>
                                  ) : actionLoading.reschedule ? (
                                    <>
                                      <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
                                      Scheduling...
                                    </>
                                  ) : (
                                    <>
                                      <CalendarIcon className="h-3 w-3 mr-1.5" />
                                      Schedule
                                    </>
                                  )}
                                </Button>
                                )
                              ) : (
                                <>
                                  {can('tests.manage') && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="flex-1"
                                    onClick={() => handleRescheduleTest(t, drawer.candidate, test?.scheduledAt)}
                                    disabled={status === "completed" || actionLoading.reschedule || !drawer.candidate || locked}
                                    title={locked ? `Not included in your ${ent?.plan_name || 'current'} plan` : undefined}
                                  >
                                    {locked ? (
                                      <>
                                        <Lock className="h-3 w-3 mr-1.5" />
                                        Not in plan
                                      </>
                                    ) : actionLoading.reschedule ? (
                                      <>
                                        <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
                                        Rescheduling...
                                      </>
                                    ) : (
                                      <>
                                        <Clock className="h-3 w-3 mr-1.5" />
                                        Reschedule
                                      </>
                                    )}
                                  </Button>
                                  )}
                                  {can('tests.manage') && (
                                  <Button 
                                    variant="outline"
                                    size="sm"
                                    className="flex-1"
                                    onClick={() => handleCancelTest(t, drawer.candidate)}
                                    disabled={status === "completed" || actionLoading.cancel || !drawer.candidate}
                                  >
                                    {actionLoading.cancel ? (
                                      <>
                                        <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
                                        Cancelling...
                                      </>
                                    ) : (
                                      <>
                                        <X className="h-3 w-3 mr-1.5" />
                                        Cancel
                                      </>
                                    )}
                                  </Button>
                                  )}
                                </>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Reschedule Modal */}
      <Dialog open={rescheduleModal.isOpen} onOpenChange={(open) => !open && setRescheduleModal({ isOpen: false, testType: null, candidate: null, currentTime: null, newTime: null })}>
        <DialogContent>
          {rescheduleModal.candidate && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {rescheduleModal.currentTime ? 'Reschedule' : 'Schedule'} {rescheduleModal.testType} Test
                </DialogTitle>
                <DialogDescription>
                {rescheduleModal.currentTime ? 'Reschedule' : 'Schedule'} the {rescheduleModal.testType} test for <strong>{rescheduleModal.candidate.name}</strong>
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="newTime">
                    {rescheduleModal.currentTime ? 'New' : ''} Scheduled Time:
                  </Label>
                  <Input
                  type="datetime-local"
                  id="newTime"
                  value={rescheduleModal.newTime || ''}
                  onChange={(e) => setRescheduleModal(prev => ({ ...prev, newTime: e.target.value }))}
                  min={new Date().toISOString().slice(0, 16)}
                />
              </div>
            </div>
              <div className="inline-flex items-center gap-2 rounded-md border bg-muted/40 px-2.5 py-1.5 text-xs text-muted-foreground">
                <Info className="h-3.5 w-3.5" />
                <span>
                  Costs <span className="font-semibold text-foreground tabular-nums">{rescheduleCost}</span>
                  {' '}credit{rescheduleCost === 1 ? '' : 's'} · Available{' '}
                  <span className="font-semibold text-foreground tabular-nums">{rescheduleAvailable}</span>
                </span>
                {!rescheduleCanAfford && (
                  <span className="ml-1 rounded bg-destructive/10 px-1.5 py-0.5 text-[11px] font-semibold text-destructive">
                    Insufficient
                  </span>
                )}
              </div>
              <DialogFooter>
                <Button 
                  variant="outline"
                onClick={() => setRescheduleModal({ isOpen: false, testType: null, candidate: null, currentTime: null, newTime: null })}
              >
                Cancel
                </Button>
                <Button 
                  variant="default"
                onClick={submitReschedule}
                disabled={!rescheduleModal.newTime || actionLoading.reschedule || !rescheduleCanAfford}
                  style={{
                    backgroundColor: getColor('primary'),
                    color: getColor('primaryForeground'),
                    borderColor: getColor('primary')
                  }}
                >
                  {actionLoading.reschedule ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {rescheduleModal.currentTime ? 'Rescheduling...' : 'Scheduling...'}
                    </>
                  ) : (
                    rescheduleModal.currentTime ? 'Reschedule Test' : 'Schedule Test'
                  )}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Candidate-selection confirmation modal */}
      <Dialog open={hireModal.isOpen} onOpenChange={(open) => !open && setHireModal({ isOpen: false, candidate: null, jobId: null })}>
        <DialogContent className="max-w-md gap-4 p-5 sm:p-6">
          {hireModal.candidate && (
            <>
              <DialogHeader className="space-y-2 text-left">
                <DialogTitle className="sr-only">Candidate selection confirmation</DialogTitle>
                <DialogDescription className="space-y-1.5 text-left">
                  <p className="text-base font-medium text-foreground">
                    Would you like to mark <strong className="font-bold text-foreground">{displayCandidateName(hireModal.candidate.name)}</strong> as selected for this role?
                  </p>
                  <p className="text-sm text-muted-foreground">This will begin the offer and onboarding process.</p>
                </DialogDescription>
              </DialogHeader>
              
              <Card className="border-slate-200 bg-slate-50/60">
                <CardContent className="grid grid-cols-1 gap-x-5 gap-y-3 p-3 sm:grid-cols-2">
                  <div className="min-w-0">
                    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Candidate</p>
                    <p className="mt-1 truncate text-sm font-semibold text-foreground">{displayCandidateName(hireModal.candidate.name)}</p>
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Location</p>
                    <p className="mt-1 truncate text-sm text-foreground">{hireModal.candidate.location || '—'}</p>
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Role</p>
                    <p className="mt-1 truncate text-sm text-foreground">{jobs.find((job) => job.id.toString() === hireModal.jobId?.toString())?.title || 'Unknown'}</p>
                  </div>
                  {(() => {
                    const app = applications.find((application) => (
                      application.candidate.id === hireModal.candidate.id
                      && application.jobId.toString() === hireModal.jobId?.toString()
                    ));
                    const overallScore = app ? weightedOverall(app.tests) : null;
                    return (
                      <div className="min-w-0">
                        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Overall Score</p>
                        <p className="mt-1 text-sm font-semibold text-foreground">{overallScore !== null ? `${overallScore}%` : '—'}</p>
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>
              
              <DialogFooter className="mt-1 flex-row justify-end gap-2 sm:justify-end">
                <Button 
                  variant="outline"
                  onClick={() => setHireModal({ isOpen: false, candidate: null, jobId: null })}
                >
                  Cancel
                </Button>
                <Button 
                  variant="default"
                  onClick={confirmHireCandidate}
                  disabled={actionLoading.hire}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  {actionLoading.hire ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Confirm Selection
                    </>
                  )}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
